import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Award, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const FAMILIES = {
  streak:   { label: "Streak", suffix: "-day streak" },
  goals:    { label: "Goals", suffix: " goal milestone" },
  workouts: { label: "Workouts", suffix: " set milestone" },
  photos:   { label: "Photos", suffix: " photo milestone" },
};

/**
 * Inject (or update) a <meta> tag in document.head. Returns a cleanup fn that
 * removes it so we don't pollute global state when this route unmounts.
 *
 * Note: this is JS-injected meta, so it only works for modern social previewers
 * that execute JavaScript (Twitter, LinkedIn, Discord, Slack). For Facebook /
 * iMessage we'd need a server-rendered HTML response — added to backlog.
 */
const setMeta = (selector, attrs) => {
  let tag = document.head.querySelector(selector);
  let created = false;
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
    document.head.appendChild(tag);
    created = true;
  } else {
    Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
  }
  return () => {
    if (created && tag.parentNode) tag.parentNode.removeChild(tag);
  };
};

/**
 * Public, unauthenticated deeplink for sharing a specific badge.
 *
 * Mounted at `/b/:type/:tier`. Renders:
 *   - Open Graph + Twitter Card meta tags pointing at the public PNG endpoint
 *     so modern previewers (Twitter, Discord, LinkedIn, Slack) show a rich
 *     preview.
 *   - A visible badge preview (the same PNG) so a user opening the link in
 *     a browser sees the achievement immediately.
 *   - A "Continue in DAIONE" CTA that deep-links into the in-app achievements
 *     page if the user is logged in, or the signup page if not.
 *
 * Fetches /api/badges/tiers (public — no auth) to validate the type/tier
 * combo and pull a human-readable label.
 */
export default function PublicBadgePage() {
  const { type, tier } = useParams();
  const tierNum = Number(tier);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [label, setLabel] = useState("");
  const [valid, setValid] = useState(null); // null = loading, true/false

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backendUrl}/api/badges/tiers`)
      .then((r) => r.json())
      .then((data) => {
        const familyTiers = data?.[type] || [];
        const match = familyTiers.find((t) => t.tier === tierNum);
        if (match) {
          setLabel(match.label);
          setValid(true);
        } else {
          setValid(false);
        }
      })
      .catch(() => setValid(false));
  }, [type, tierNum]);

  // Inject OG meta tags + restore page title on unmount.
  useEffect(() => {
    if (!valid || !label) return undefined;
    const meta = FAMILIES[type] || { label: "Achievement", suffix: " milestone" };
    const cardUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/badge-card?type=${type}&tier=${tierNum}`;
    const pageUrl = window.location.href;
    const title = `${label} · DAIONE`;
    const description = `I just unlocked the ${label} badge on DAIONE. Train. Track. Transform.`;
    const prevTitle = document.title;
    document.title = title;
    const cleanups = [
      setMeta('meta[name="description"]', { name: "description", content: description }),
      setMeta('meta[property="og:title"]', { property: "og:title", content: title }),
      setMeta('meta[property="og:description"]', { property: "og:description", content: description }),
      setMeta('meta[property="og:image"]', { property: "og:image", content: cardUrl }),
      setMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1080" }),
      setMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "1080" }),
      setMeta('meta[property="og:url"]', { property: "og:url", content: pageUrl }),
      setMeta('meta[property="og:type"]', { property: "og:type", content: "website" }),
      setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "DAIONE" }),
      setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }),
      setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title }),
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description }),
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: cardUrl }),
    ];
    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn?.());
    };
  }, [valid, label, type, tierNum]);

  if (valid === false) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4" data-testid="public-badge-not-found">
        <div className="max-w-md text-center">
          <div className="label-eyebrow text-[#6B9BFF] mb-4">Badge not found</div>
          <h1 className="font-display text-3xl mb-4">This link is broken</h1>
          <p className="text-white/60 text-sm mb-8">
            The badge you're looking for doesn't exist.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> DAIONE home
          </Link>
        </div>
      </div>
    );
  }

  const meta = FAMILIES[type] || { label: "Achievement", suffix: " milestone" };
  const cardUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/badge-card?type=${type}&tier=${tierNum}`;

  const handleContinue = () => {
    if (user) navigate("/app/achievements");
    else navigate("/auth?mode=signup");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="public-badge-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/40 hover:text-white mb-8"
          data-testid="public-badge-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <Award className="w-4 h-4 text-[#6B9BFF]" />
          <div className="label-eyebrow text-[#6B9BFF]">Achievement unlocked</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mb-8" data-testid="public-badge-title">
          {label || `${tierNum} ${meta.label}`}
        </h1>

        <div className="flex justify-center mb-10">
          <div className="relative max-w-md w-full aspect-square border border-white/10 bg-[#121212]">
            <img
              src={cardUrl}
              alt={label || `${tierNum} ${meta.label}`}
              className="w-full h-full object-cover"
              data-testid="public-badge-image"
            />
          </div>
        </div>

        <div className="text-center">
          <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">
            DAIONE turns your training, nutrition, and recovery into a daily ritual —
            and earns you a badge every time you keep showing up.
          </p>
          <button
            type="button"
            onClick={handleContinue}
            className="inline-flex items-center gap-2 bg-[#6B9BFF] text-black font-bold uppercase tracking-wider text-xs px-6 py-3 hover:bg-white transition-colors"
            data-testid="public-badge-cta"
          >
            {user ? "Open in DAIONE" : "Join DAIONE"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
